import React, { useState, useEffect } from 'react';
import { TextField, Button, Box, Typography, Chip } from '@mui/material';
import {
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';

export default function AssignMultipleModal({
  open,
  column,
  relatedData,
  rows,
  handleClose,
  handleSave,
  add_title="Assign",
}) {
  const [formData, setFormData] = useState([]);
  const [addErrors, setAddErrors] = useState({});

  // Helper function to extract IDs from payload format
  const extractFlatIdList = (payloadList) => {
    if (!Array.isArray(payloadList)) return [];
    return payloadList.map(item => item[column.attr]);
  };

  // Helper function to convert IDs back to payload format
  const getPayloadFormat = (flatList) => {
    if (!Array.isArray(flatList)) return [];
    return flatList.map(itemId => ({ [column.attr]: itemId }));
  };

  useEffect(() => {
    setFormData(extractFlatIdList(rows));
    setAddErrors({});
  }, [column, relatedData, rows, open]);

  const handleChange = (fieldAttr, value) => {
    setFormData(value);

    // Clear error when user edits the field
    setAddErrors((prevErrors) => ({
      ...prevErrors,
      [fieldAttr]: undefined,
    }));
  };

  const handleSaveWithTransform = () => {
    // Transform data back to the expected format
    const transformedData = getPayloadFormat(formData);
    handleSave(transformedData, handleSuccess, handleAddFailure);
  };

  const handleSuccess = () => {
    // Clear or update values in the form as needed
    setAddErrors({});
  };

  const handleAddFailure = (errorDict) => {
    console.log(errorDict);
    // Highlight errors
    setAddErrors(errorDict); // Set errors from backend
  };

  // Safety check to ensure we have the required data
  if (!column || !relatedData || !relatedData[column.attr]) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 24
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
        <Typography variant="h3">{add_title}</Typography>
        {true && ( // ToDo: !isSubmitting
          <IconButton edge="end" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <Divider />
      
      <DialogContent>
        <Box
          component="form"
          noValidate
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mb: 1,
            gap: 2,
          }}
        >
          <Box key={column.attr}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="h5" gutterBottom>
                {column.label}
                {column.required && (
                  <Typography component="span" color="error" ml={0.5}>
                    *
                  </Typography>
                )}
              </Typography>
              <Tooltip title={column.help_text} arrow>
                <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'pointer', mt: '-2px' }} />
              </Tooltip>
            </Box>
            <FormControl
              fullWidth
              error={Boolean(addErrors[column.attr])}
            >
              <Select
                id={column.attr}
                multiple
                value={formData || []}
                onChange={(e) => handleChange(column.attr, e.target.value)}
                fullWidth
                displayEmpty
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 ? (
                      <em style={{ color: '#666' }}>Select {column.label}</em>
                    ) : (
                      selected.map((value) => (
                        <Chip
                          key={value}
                          label={relatedData[column.attr][value]?.label || value}
                          size="small"
                        />
                      ))
                    )}
                  </Box>
                )}
              >
                {Object.entries(relatedData[column.attr]).map(
                  ([option_key, option_value]) => {
                    const isSelected = (formData || []).includes(option_value.id);
                    return (
                      <MenuItem
                        key={option_value.id}
                        value={option_value.id}
                        sx={{
                          backgroundColor: isSelected ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                          '&:hover': {
                            backgroundColor: isSelected ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                          <span>{option_value.label}</span>
                          {isSelected && (
                            <CheckIcon
                              sx={{
                                color: 'primary.main',
                                fontSize: '1.2rem',
                                ml: 1,
                              }}
                            />
                          )}
                        </Box>
                      </MenuItem>
                    );
                  }
                )}
              </Select>
              {addErrors[column.attr] && (
                <FormHelperText>
                  {addErrors[column.attr]?.[0]}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSaveWithTransform}
          variant="contained"
          color="primary"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
